import { notification } from 'antd';
import type { AxiosError } from 'axios';
import type { ActionsObservable } from 'redux-observable';
import { ofType } from 'redux-observable';
import { of } from 'rxjs';
import { catchError, concatMap, exhaustMap, map, switchMap } from 'rxjs/operators';
import { IntegrationKey } from '../../pages/integrations/shared/models';
import type {
  AuthenticateJDOC,
  AuthenticateTrapView,
  AuthenticateTrapViewExternal,
  GetAccessTrapView,
  GetAreasDiffJDOC,
  GetAuthenticateJDOC,
  GetAuthenticatePerfectFlight,
  GetAuthenticateTrapView,
  GetFarmsByOrganizationJDOC,
  GetOrganizationsJDOC,
  GetPerfectFlightApplications,
  GetPerfectFlightAreasDiff,
  GetPermissionsJDOC,
  GetTrapViewBinding,
  GetTrapViewDevices,
  GetUpdateDiffJDOC,
  InstallTrapView,
  IntegratePerfectFlight,
  LoadPerfectFlightPropertyList,
  PostAreasDiffJDOC,
  PostPermissionsJDOC,
  RevokeAccessJDOC,
  RevokePerfectFlight,
  RevokeTrapView
} from './integrations.actions';
import {
  AuthenticateJDOCFailure,
  AuthenticateJDOCSuccess,
  AuthenticateTrapViewExternalFailure,
  AuthenticateTrapViewExternalSuccess,
  AuthenticateTrapViewFailure,
  AuthenticateTrapViewSuccess,
  GetAccessTrapViewFailure,
  GetAccessTrapViewSuccess,
  GetAreasDiffJDOCFailure,
  GetAreasDiffJDOCSuccess,
  GetAuthenticateJDOCFailure,
  GetAuthenticateJDOCSuccess,
  GetAuthenticatePerfectFlightFailure,
  GetAuthenticatePerfectFlightSuccess,
  GetAuthenticateTrapViewFailure,
  GetAuthenticateTrapViewSuccess,
  GetFarmsByOrganizationJDOCFailure,
  GetFarmsByOrganizationJDOCSuccess,
  GetOrganizationsJDOCFailure,
  GetOrganizationsJDOCSuccess,
  GetPerfectFlightApplicationsFailure,
  GetPerfectFlightApplicationsSuccess,
  GetPerfectFlightAreasDiffFailure,
  GetPerfectFlightAreasDiffSuccess,
  GetPermissionsJDOCFailure,
  GetPermissionsJDOCSuccess,
  GetTrapViewBindingFailure,
  GetTrapViewBindingSuccess,
  GetTrapViewDevicesFailure,
  GetTrapViewDevicesSuccess,
  GetUpdateDiffJDOCFailure,
  GetUpdateDiffJDOCSuccess,
  InstallTrapViewFailure,
  InstallTrapViewSuccess,
  IntegratePerfectFlightFailure,
  IntegratePerfectFlightSuccess,
  IntegrationsActionsTypes,
  LoadPerfectFlightPropertyListFailure,
  LoadPerfectFlightPropertyListSuccess,
  PostAreasDiffJDOCFailure,
  PostAreasDiffJDOCSuccess,
  PostPermissionsJDOCFailure,
  PostPermissionsJDOCSuccess,
  RevokeAccessJDOCFailure,
  RevokeAccessJDOCSuccess,
  RevokePerfectFlightFailure,
  RevokePerfectFlightSuccess,
  RevokeTrapViewFailure,
  RevokeTrapViewSuccess
} from './integrations.actions';
import { TrapviewInstallationError } from '@protector/entities/integration/constants';
import {
  authenticateJDOC,
  getAccessTrapView,
  getAreasDiffJDOC,
  getAuthenticateJDOC,
  getAuthenticateStatusPerfectFlight,
  getAuthenticateTrapView,
  getFarmsByOrganizationsJDOC,
  getOrganizationsJDOC,
  getPerfectFlightApplications,
  getPerfectFlightAreasDiff,
  getPerfectFlightProperties,
  getPermissionsJDOC,
  getTrapViewBinding,
  getTrapViewDevices,
  postAreasDiffJDOC,
  postInstallTrapView,
  postIntegratePerfectFlight,
  postIntegrateTrapView,
  postIntegrateTrapViewExternal,
  postPermissionsJDOC,
  postUpdateDiffJDOC,
  revokeAccessJDOC,
  revokePerfectFlightAccess,
  revokeTrapView
} from './integrations.service';
import { mapResponseToAreaDiffResponse } from './integrations.utils';

export const handleAuthenticateJDOC = (action$: ActionsObservable<ReturnType<typeof AuthenticateJDOC>>) =>
  action$.pipe(
    ofType(IntegrationsActionsTypes.AUTHENTICATE_JDOC),
    map(action => action.payload),
    exhaustMap(({ url, propertyId }) => authenticateJDOC(url, propertyId)),
    map(result => {
      return AuthenticateJDOCSuccess(result.data.url);
    }),
    catchError((error: string) => of(AuthenticateJDOCFailure(error)))
  );

export const handleRevokeAccessJDOC = (action$: ActionsObservable<ReturnType<typeof RevokeAccessJDOC>>) =>
  action$.pipe(
    ofType(IntegrationsActionsTypes.REVOKE_ACCESS_JDOC),
    map(action => action.payload),
    exhaustMap(payload => revokeAccessJDOC(payload)),
    map(() => {
      return RevokeAccessJDOCSuccess();
    }),
    catchError((error: string) => of(RevokeAccessJDOCFailure(error)))
  );

export const handleLoadPerfectFlightPropertyList = (action$: ActionsObservable<ReturnType<typeof LoadPerfectFlightPropertyList>>) =>
  action$.pipe(
    ofType(IntegrationsActionsTypes.LOAD_PERFECT_FLIGHT_PROPERTY_LIST),
    map(action => action.payload),
    switchMap(payload =>
      getPerfectFlightProperties(payload).pipe(
        map(result => {
          return LoadPerfectFlightPropertyListSuccess(result.data.filter(d => d.plots_count > 0));
        }),
        catchError((err: AxiosError) => {
          let errorMessage: string;

          if (err?.message?.includes('400')) {
            errorMessage = 'perfectFlight.get_failed';
          } else {
            errorMessage = 'perfectFlight.integrate_failed';
          }
          return of(LoadPerfectFlightPropertyListFailure(errorMessage));
        })
      )
    )
  );

export const handleIntegratePerfectFlight = (action$: ActionsObservable<ReturnType<typeof IntegratePerfectFlight>>) =>
  action$.pipe(
    ofType(IntegrationsActionsTypes.INTEGRATE_PERFECT_FLIGHT),
    map(action => action.payload),
    switchMap(payload =>
      postIntegratePerfectFlight(payload).pipe(
        map(() => {
          return IntegratePerfectFlightSuccess();
        }),
        catchError((error: string) => of(IntegratePerfectFlightFailure(error)))
      )
    )
  );

export const handleGetAuthenticatePerfectFlight = (action$: ActionsObservable<ReturnType<typeof GetAuthenticatePerfectFlight>>) =>
  action$.pipe(
    ofType(IntegrationsActionsTypes.GET_AUTHENTICATE_PERFECT_FLIGHT),
    map(action => action.payload),
    switchMap(payload =>
      getAuthenticateStatusPerfectFlight(payload).pipe(
        map(result => {
          return GetAuthenticatePerfectFlightSuccess(result.data);
        }),
        catchError((error: string) => of(GetAuthenticatePerfectFlightFailure(error)))
      )
    )
  );

export const handleGetAuthenticateJDOC = (action$: ActionsObservable<ReturnType<typeof GetAuthenticateJDOC>>) =>
  action$.pipe(
    ofType(IntegrationsActionsTypes.GET_AUTHENTICATE_JDOC),
    map(action => action.payload),
    exhaustMap(payload => getAuthenticateJDOC(payload)),
    map(result => {
      return GetAuthenticateJDOCSuccess(result.data);
    }),
    catchError((error: string) => of(GetAuthenticateJDOCFailure(error)))
  );

export const handleGetPermissionsJDOC = (action$: ActionsObservable<ReturnType<typeof GetPermissionsJDOC>>) =>
  action$.pipe(
    ofType(IntegrationsActionsTypes.GET_PERMISSIONS_JDOC),
    map(action => action.payload),
    exhaustMap(payload => getPermissionsJDOC(payload)),
    map(result => {
      return GetPermissionsJDOCSuccess(result.data);
    }),
    catchError((error: string) => of(GetPermissionsJDOCFailure(error)))
  );

export const handlePostPermissionsJDOC = (action$: ActionsObservable<ReturnType<typeof PostPermissionsJDOC>>) =>
  action$.pipe(
    ofType(IntegrationsActionsTypes.POST_PERMISSIONS_JDOC),
    map(action => action.payload),
    concatMap(payload => {
      return postPermissionsJDOC(payload).pipe(
        map(() => {
          return PostPermissionsJDOCSuccess();
        }),
        catchError((error: string) => of(PostPermissionsJDOCFailure(error)))
      );
    })
  );

export const handleGetOrganizationsJDOC = (action$: ActionsObservable<ReturnType<typeof GetOrganizationsJDOC>>) =>
  action$.pipe(
    ofType(IntegrationsActionsTypes.GET_ORGANIZATIONS_JDOC),
    map(action => action.payload),
    concatMap(payload => {
      return getOrganizationsJDOC(payload).pipe(
        map(result => {
          return GetOrganizationsJDOCSuccess(result);
        }),
        catchError((error: string) => of(GetOrganizationsJDOCFailure(error)))
      );
    })
  );

export const handleGetFarmsByOrganizationsJDOC = (action$: ActionsObservable<ReturnType<typeof GetFarmsByOrganizationJDOC>>) =>
  action$.pipe(
    ofType(IntegrationsActionsTypes.GET_FARMS_BY_ORGANIZATIONS_JDOC),
    map(action => action.payload),
    concatMap(payload => {
      return getFarmsByOrganizationsJDOC(payload.organizationId, payload.propertyId).pipe(
        map(result => {
          return GetFarmsByOrganizationJDOCSuccess(result);
        }),
        catchError((error: string) => of(GetFarmsByOrganizationJDOCFailure(error)))
      );
    })
  );

export const handleRevokePerfectFlight = (action$: ActionsObservable<ReturnType<typeof RevokePerfectFlight>>) =>
  action$.pipe(
    ofType(IntegrationsActionsTypes.REVOKE_PERFECT_FLIGHT),
    map(action => action.payload),
    concatMap(payload => {
      return revokePerfectFlightAccess(payload).pipe(
        map(() => {
          return RevokePerfectFlightSuccess();
        }),
        catchError((error: string) => of(RevokePerfectFlightFailure(error)))
      );
    })
  );
export const handleGetAreasDiffPerfectFlight = (action$: ActionsObservable<ReturnType<typeof GetPerfectFlightAreasDiff>>) =>
  action$.pipe(
    ofType(IntegrationsActionsTypes.GET_AREAS_DIFF_PERFECT_FLIGHT),
    map(action => action.payload),
    concatMap(payload => {
      return getPerfectFlightAreasDiff(payload.propertyId, payload.pfFarmId).pipe(
        map(result => {
          return GetPerfectFlightAreasDiffSuccess(mapResponseToAreaDiffResponse(result.data, IntegrationKey.PERFECT_FLIGHT));
        }),
        catchError((error: string) => of(GetPerfectFlightAreasDiffFailure(error)))
      );
    })
  );

export const handleGetAreasDiffJDOC = (action$: ActionsObservable<ReturnType<typeof GetAreasDiffJDOC>>) =>
  action$.pipe(
    ofType(IntegrationsActionsTypes.GET_AREAS_DIFF_JDOC),
    map(action => action.payload),
    concatMap(payload => {
      return getAreasDiffJDOC(payload.organizationId, payload.farmId, payload.propertyId).pipe(
        map(result => {
          return GetAreasDiffJDOCSuccess(mapResponseToAreaDiffResponse(result, IntegrationKey.JOHN_DEERE));
        }),
        catchError((error: string) => of(GetAreasDiffJDOCFailure(error)))
      );
    })
  );

export const handlePostAreasDiffJDOC = (action$: ActionsObservable<ReturnType<typeof PostAreasDiffJDOC>>) =>
  action$.pipe(
    ofType(IntegrationsActionsTypes.POST_AREAS_DIFF_JDOC),
    map(action => action.payload),
    concatMap(payload => {
      return postAreasDiffJDOC(payload.organizationId, payload.farmId, payload.propertyId, payload.data).pipe(
        map(() => {
          return PostAreasDiffJDOCSuccess();
        }),
        catchError((error: string) => of(PostAreasDiffJDOCFailure(error)))
      );
    })
  );

export const handleGetUpdateDiffJDOC = (action$: ActionsObservable<ReturnType<typeof GetUpdateDiffJDOC>>) =>
  action$.pipe(
    ofType(IntegrationsActionsTypes.GET_UPDATE_DIFF),
    map(action => action.payload),
    concatMap(payload => {
      return postUpdateDiffJDOC(payload).pipe(
        map(result => {
          return GetUpdateDiffJDOCSuccess(result);
        }),
        catchError((error: string) => of(GetUpdateDiffJDOCFailure(error)))
      );
    })
  );

export const handleGetPerfectFlightApplications = (action$: ActionsObservable<ReturnType<typeof GetPerfectFlightApplications>>) =>
  action$.pipe(
    ofType(IntegrationsActionsTypes.GET_PERFECT_FLIGHT_APPLICATIONS),
    map(action => action.payload),
    concatMap(payload => {
      return getPerfectFlightApplications(payload.propertyId).pipe(
        map(result => {
          return GetPerfectFlightApplicationsSuccess(result.data);
        }),
        catchError((error: string) => of(GetPerfectFlightApplicationsFailure(error)))
      );
    })
  );

export const handleGetAuthenticateTrapView = (action$: ActionsObservable<ReturnType<typeof GetAuthenticateTrapView>>) =>
  action$.pipe(
    ofType(IntegrationsActionsTypes.GET_AUTHENTICATE_TRAP_VIEW),
    map(action => action.payload),
    concatMap(payload => {
      return getAuthenticateTrapView(payload).pipe(
        map(result => {
          return GetAuthenticateTrapViewSuccess(result.data);
        }),
        catchError((error: string) => of(GetAuthenticateTrapViewFailure(error)))
      );
    })
  );

export const handleGetAccessTrapView = (action$: ActionsObservable<ReturnType<typeof GetAccessTrapView>>) =>
  action$.pipe(
    ofType(IntegrationsActionsTypes.GET_TRAP_VIEW_ACCESS),
    map(action => action.payload),
    concatMap(payload => {
      return getAccessTrapView(payload).pipe(
        map(result => {
          return GetAccessTrapViewSuccess(result.data);
        }),
        catchError((error: string) => of(GetAccessTrapViewFailure(error)))
      );
    })
  );

export const handlePostIntegrateTrapViewExternal = (action$: ActionsObservable<ReturnType<typeof AuthenticateTrapViewExternal>>) =>
  action$.pipe(
    ofType(IntegrationsActionsTypes.AUTHENTICATE_TRAP_VIEW_EXTERNAL),
    map(action => action.payload),
    concatMap(payload => {
      return postIntegrateTrapViewExternal(payload).pipe(
        map(result => {
          return AuthenticateTrapViewExternalSuccess(result.data);
        }),
        catchError((error: string) => of(AuthenticateTrapViewExternalFailure(error)))
      );
    })
  );

export const handlePostIntegrateTrapView = (action$: ActionsObservable<ReturnType<typeof AuthenticateTrapView>>) =>
  action$.pipe(
    ofType(IntegrationsActionsTypes.AUTHENTICATE_TRAP_VIEW),
    map(action => action.payload),
    concatMap(payload => {
      return postIntegrateTrapView(payload).pipe(
        map(result => {
          return AuthenticateTrapViewSuccess(result.data);
        }),
        catchError((error: string) => of(AuthenticateTrapViewFailure(error)))
      );
    })
  );

export const handleRevokeTrapView = (action$: ActionsObservable<ReturnType<typeof RevokeTrapView>>) =>
  action$.pipe(
    ofType(IntegrationsActionsTypes.REVOKE_TRAP_VIEW),
    map(action => action.payload),
    concatMap(payload => {
      return revokeTrapView(payload).pipe(
        map(result => {
          return RevokeTrapViewSuccess(result.data);
        }),
        catchError((error: string) => of(RevokeTrapViewFailure(error)))
      );
    })
  );

export const handleGetTrapViewDevices = (action$: ActionsObservable<ReturnType<typeof GetTrapViewDevices>>) =>
  action$.pipe(
    ofType(IntegrationsActionsTypes.GET_TRAP_VIEW_DEVICES),
    map(action => action.payload),
    concatMap(payload => {
      return getTrapViewDevices(payload).pipe(
        map(result => {
          if (Array.isArray(result.data)) {
            return GetTrapViewDevicesSuccess(result.data);
          }
          return GetTrapViewDevicesFailure('Unexpected response');
        }),
        catchError((error: string) => of(GetTrapViewDevicesFailure(error)))
      );
    })
  );

export const handleGetTrapViewBinding = (action$: ActionsObservable<ReturnType<typeof GetTrapViewBinding>>) =>
  action$.pipe(
    ofType(IntegrationsActionsTypes.GET_TRAP_VIEW_BINDING),
    map(action => action.payload),
    concatMap(payload => {
      return getTrapViewBinding(payload).pipe(
        map(result => {
          return GetTrapViewBindingSuccess(result.data);
        }),
        catchError((error: string) => of(GetTrapViewBindingFailure(error)))
      );
    })
  );

export const handlePostInstallTrapView = (action$: ActionsObservable<ReturnType<typeof InstallTrapView>>) =>
  action$.pipe(
    ofType(IntegrationsActionsTypes.INSTALL_TRAP_VIEW),
    map(action => action.payload),
    concatMap(payload => {
      return postInstallTrapView(payload.trapviewDevice).pipe(
        map(() => {
          notification.success(payload.callback.notification.success);
          payload.callback.tracking?.integratedTrapview();
          payload.callback.clearDrawerAndSelectStation();
          return InstallTrapViewSuccess();
        }),
        catchError((error: AxiosError<TrapviewInstallationError[]>) => {
          if (error.response?.data.includes(TrapviewInstallationError.NOT_FOUND_PEST_CODE))
            notification.error(payload.callback.notification.pestCodeError);
          else notification.error(payload.callback.notification.error);
          return of(InstallTrapViewFailure(error as unknown as string));
        })
      );
    })
  );
